import { Injectable } from '@angular/core';
import { AppConstants, LMDialogButtonKey, LMTimeStampDirective, LMTransformOptions, LMUniqueIdService } from '@utils/index';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import * as _ from 'lodash';
import { LMListSpecUtilService } from './lm-list-spec-util.service';
import { Observable } from 'rxjs';
import { SohoMessageService } from 'ids-enterprise-ng';
import { LMContextService } from '@services/lm-context.service';
import { LMGlobalPersonalizationService } from '@app/states/services/lm-global-personalization.service';

@Injectable()
export class LMListUtilService {

  constructor(
    private lmFormatUtilService: LMFormatUtilService,
    private lmListSpecUtilService: LMListSpecUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    private sohoMessageService: SohoMessageService,
    private lmContextService: LMContextService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService
  ) {}

  public setSpecFields(dataViewSet: LMDataViewSet): void {
    // Pass down stateValues and type properties to each dataView from the header dataview.
    const headerDataView: LMDataView = dataViewSet.header;
    const data: Array<LMDataView> = dataViewSet.data.map((dataView: LMDataView) => {
      for (const fieldId in dataView.fields) {
        if (!dataView.fields[ fieldId ] || !headerDataView.fields[ fieldId ]) {
          continue;
        }

        if (headerDataView.fields[ fieldId ].type) {
          dataView.fields[ fieldId ].type = headerDataView.fields[ fieldId ].type;
        }

        // Add state fields if they don't already  exist
        if (dataView.fields[ fieldId ].stateValues === undefined && headerDataView.fields[ fieldId ].stateValues) {
          dataView.fields[ fieldId ].stateValues = headerDataView.fields[ fieldId ].stateValues;
        }

      }
      return dataView;
    });

    // return data;
  }

  public areTheSameJson(json1: any, json2: any) {
    if (!json1 && !json2) {
      return true;
    }

    if ((!json1 && json2) || (json1 && !json2)) {
      return false;
    }

    const a = JSON.stringify(json1);
    const b = JSON.stringify(json2);
    return a === b;
  }

  public areTheSame(array1: Array<any>, array2: Array<any>): boolean {
    if (!array1) {
      array1 = [];
    }

    if (!array2) {
      array2 = [];
    }
    const a = JSON.stringify(array1);
    const b = JSON.stringify(array2);

    return a === b;
  }

  public buildSearchTags(filterCriteria: Array<LMFilterFieldCriteria>): Array<LMListSearchTag> {
    if (!filterCriteria) {
      return [];
    }

    return filterCriteria?.filter( criteria => (criteria?.operator === 'isEmpty' || criteria?.operator === 'isNotEmpty' || criteria.value !== '' ))
      .map(item => this.filterCriteriaToSearchTag(item));
  }

  public buildDetailFieldsAsTooltip(data: LMDataView, detailFieldsArray: Array<LMListDisplayField>): string {
    let tooltip: string;
    if (data && detailFieldsArray) {
      const detailFields: Array<string> = [];
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for ( let index = 0; index < detailFieldsArray.length; index++) {
        let detailLabel = '';
        if (detailFieldsArray[index].label) {
          detailLabel = this.lmFormatUtilService.formatMessageRule(detailFieldsArray[ index ].label, data);
        }
        const detailValue = data.fields[detailFieldsArray[index].fieldId].value;
        let row: string;
        if ( detailFieldsArray[index].type === 'BINARYDOCUMENT') {
          row = `<td style="text-align: right; width: 60%; padding-bottom: 5px"><img src="${detailValue}" alt=""/></td></tr>`;
        } else {
          row = `<tr><td style="text-align: left; width: 40%; word-wrap: normal;">${detailLabel}</td>`+
            `<td style="text-align: right; width: 60%; word-wrap: normal;">${detailValue}</td></tr>`;
        }
        detailFields.push(row);
      }
      tooltip = '<table><tbody>';
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < detailFields.length; index++) {
        tooltip = tooltip + detailFields[index];
      }
      tooltip = tooltip + '</tbody></table>';
    }
    return tooltip;
  }

  public buildSpecFields(displayFields: Array<LMListDisplayField>|LMListDisplayField): LMSpecFields {
    const specFields = {};

    if (displayFields instanceof Array) {
      for (let i = 0, length = displayFields.length; i < length; i++) {
        const fieldId = displayFields[ i ].fieldId;
        if (fieldId) {
          specFields[ fieldId ] = _.cloneDeep( displayFields[ i ]);
          specFields[ fieldId ].stateValues = specFields[ fieldId ].states;
        }
      }
    } else if (displayFields instanceof Object) {
      for (const field in displayFields) {
        if (displayFields.hasOwnProperty(field)) {
          specFields[ field ] = _.cloneDeep(displayFields[ field ]);
        }
      }
    }

    return specFields;
  }

  public mergeStickyModifier(filterCriteria: LMFilterFieldCriteria[], filterModifiers: Map<string, string>, isSearchForm: boolean) {
    // TODO: Currently, list with search form uses fieldId as key to store changes in filter ops
    // while list with filter bar uses columnId. Can't combine this yet because I found an issue when the field is
    // displayed multiple times in a list, might be a conflict. Will address this in LMCLIENT-38869
    const key = isSearchForm ? 'fieldId' : 'id';
    for(const id of filterModifiers.keys()) {
      const operator = filterModifiers.get(id);
      if (!filterCriteria.some( fieldCriteria => fieldCriteria[key] === id )) {
        const newEntry = {
          // [key]: id,
          value: '',
          operator
        };
        newEntry[key] = id;
        filterCriteria.push(newEntry as LMFilterFieldCriteria);
      }
    }

    return filterCriteria;
  }

  public filterCriteriaToSearchTag(filterCriteria: LMFilterFieldCriteria): LMListSearchTag {
    let displayValue = filterCriteria.displayValue;
    if (Array.isArray(displayValue)) {
      displayValue = (displayValue).join(AppConstants.MULTI_SEARCH_TERM_DELIMITER);
    }
    let operatorLabel = '';
    let useFilterTypeNoValue = false;

    switch (filterCriteria.operator) {
      case 'equals':
        operatorLabel = 'LabelEquals';
        break;
      case 'doesNotEqual':
        operatorLabel = 'LabelDoesNotEqual';
        break;
      case 'contains':
        operatorLabel = 'LabelContains';
        break;
      case 'doesNotContain':
        operatorLabel = 'LabelDoesNotContain';
        break;
      case 'lessThan':
        operatorLabel = 'LabelLessThan';
        break;
      case 'lessThanOrEquals':
        operatorLabel = 'LabelLessOrEquals';
        break;
      case 'greaterThan':
        operatorLabel = 'LabelGreaterThan';
        break;
      case 'greaterThanOrEquals':
        operatorLabel = 'LabelGreaterOrEquals';
        break;
      case 'endsWith':
        operatorLabel = 'LabelEndsWith';
        break;
      case 'doesNotEndWith':
        operatorLabel = 'LabelDoesNotEndWith';
        break;
      case 'startsWith':
        operatorLabel = 'LabelStartsWith';
        break;
      case 'doesNotStartWith':
        operatorLabel = 'LabelDoesNotStartWith';
        break;
      case 'inRange':
        operatorLabel = 'LabelInRange';
        break;
      case 'isEmpty':
        displayValue = Soho.Locale.translate('Empty');
        useFilterTypeNoValue = true;
        break;
      case 'isNotEmpty' :
        displayValue = Soho.Locale.translate('NotEmpty');
        useFilterTypeNoValue = true;
        break;
      default:
        break;
    }

    let label = filterCriteria.label;
    if (operatorLabel) {
      label = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(operatorLabel), [ label ]);
    }

    return {
      label,
      id: filterCriteria.fieldId,
      tag: displayValue,
      isRequired: filterCriteria.isRequired,
      isDisplayOnly: filterCriteria.isDisplayOnly,
      useFilterTypeNoValue
    };
  }

  public buildSelectionTags(multiSelectedValues: Array<LMMultiSelectedValue>): Array<LMListSelectionTag> {
    if (!multiSelectedValues) {
      return [];
    }

    return multiSelectedValues.map(item => this.multiSelectionValueToSelectionTag(item));
  }

  public multiSelectionValueToSelectionTag(multiSelectedValue: LMMultiSelectedValue): LMListSelectionTag {
    return {
      id: multiSelectedValue.fieldId,
      tag: multiSelectedValue.formattedValue
    };
  }

  public buildSohoCondition(filterCriteria: LMFilterFieldCriteria, gridColumns: ListGridColumn[]): SohoDataGridFilterCondition {
    const filterColumn: ListGridColumn = gridColumns.find(gridColumn => !gridColumn.hidden && gridColumn.fieldId && (gridColumn.id === filterCriteria.id
      || gridColumn.fieldId === filterCriteria.fieldId || gridColumn.id === filterCriteria.compactFormatColumnId));

    if (!filterColumn) {
      return null; // safety
    }

    let value = filterCriteria.value;

    // need to convert the internal date value to a display one, for LMCLIENT-20541
    if (filterColumn.filterType === 'date') {
      if (filterCriteria.operator === 'inRange') {
        value = value.replace(',', ' - ');
      }
      switch (filterColumn.dateFormat) {
        case Soho.Locale.calendar().dateFormat.short + ' '
        + Soho.Locale.calendar().dateFormat.timestamp:
          // Timestamp
          value = this.lmFormatUtilService.timestampToDisplay(value);
          break;
        case Soho.Locale.calendar().dateFormat.short:
          value = this.lmFormatUtilService.yyyymmddToDisplay(value);
          break;
        case Soho.Locale.calendar().dateFormat.month:
          value = this.lmFormatUtilService.mmddToDisplay(value);
          break;
        case Soho.Locale.calendar().dateFormat.year:
          value = this.lmFormatUtilService.yyyymmToDisplay(value);
          break;
        case 'yyyy':
          value = this.lmFormatUtilService.yyyyToDisplay(value);
          break;
      }
    } else if (filterColumn.filterType === 'time') {
      value = this.lmFormatUtilService.timeToDisplay(value);
    } else if (filterCriteria.isCompactFormatField) {
      value = filterCriteria.compactFormDisplayValue;
    } else if (filterColumn.filterType === 'multiselect') {
      value = filterCriteria.value;
    } else if (filterColumn.displayField.type === AppConstants.PERCENT) {
      const transformOptions: LMTransformOptions = {
        decSize: filterColumn.displayField.decSize,
        precision: filterColumn.displayField.decSize,
        displayZero: true
      };
      if (Array.isArray(value)) {
        const filterValue = value;
        value = '';
        filterValue.map((val, index, array) => {
          const delimiter = (index === array.length -1) ? '' : AppConstants.MULTI_SEARCH_TERM_DELIMITER;
          value += this.lmFormatUtilService.percentToFilterDisplay(val, transformOptions) + delimiter;
        });
      } else {
        value = this.lmFormatUtilService.percentToDisplay(value, transformOptions);
      }
    } else if (filterColumn.displayField.type === AppConstants.BCD) {
      const transformOptions: LMTransformOptions = {
        decSize: filterColumn.displayField.decSize,
        precision: filterColumn.displayField.decSize,
        displayZero: true
      };
      if (Array.isArray(value)) {
        const filterValue = value;
        value = '';
        filterValue.map((val, index, array) => {
          const delimiter = (index === array.length -1) ? '' : AppConstants.MULTI_SEARCH_TERM_DELIMITER;
          value += this.lmFormatUtilService.bcdToFilterDisplay(val, transformOptions) + delimiter;
        });
      } else {
        value = this.lmFormatUtilService.bcdToDisplay(value, transformOptions);
      }
    } else if (filterColumn.displayField.type === AppConstants.NUMERIC) {
      if (Array.isArray(value)) {
        const filterValue = value;
        value = '';
        filterValue.map((val, index, array) => {
          const delimiter = (index === array.length -1) ? '' : AppConstants.MULTI_SEARCH_TERM_DELIMITER;
          value += val + delimiter;
        });
      }
    } else if (filterColumn.filterType === 'lookup' || filterColumn.filterType === 'text') {
      if (Array.isArray(value)) {
        const filterValue = value;
        value = '';
        filterValue.map((val, index, array) => {
          const delimiter = (index === array.length -1) ? '' : AppConstants.MULTI_SEARCH_TERM_DELIMITER;
          value += val + delimiter;
        });
      }
    }

    const sohoCondition: SohoDataGridFilterCondition = {
      columnId: filterColumn.id,
      operator: this.getSohoOperator(filterCriteria.operator),
      value
    };

    return sohoCondition;
  }

  /**
   * Updates the column labels on a datagrid. This must be called before data is loaded into the data
   * grid with the gridOptions.source function. Otherwise the pager bar gets screwed up. Here is the
   * soho ticket for this: https://jira.infor.com/browse/SOHO-6990
   */
  public updateColumnLabels(
    listSpec: LMListSpec,
    dataViewSet: LMDataViewSet,
    gridColumns: ListGridColumn[],
    columnGroups: SohoDataGridColumnGroup[]
  ): boolean {
    let hasUpdate = false;

    // ----------------------------------------------------------
    // loop through columns looking for a message rule with
    // variables. If found then format the value based on the
    // message rule and dataViewSet.
    // ----------------------------------------------------------
    if (gridColumns) {
      for (const gridColumn of gridColumns) {
        if (gridColumn.displayField ) {
          const property = gridColumn.displayField.label ? 'label' : gridColumn.displayField.title ? 'title' : '';
          const columnName = property ? gridColumn.displayField[property] : '';
          const hasVariables = columnName && columnName.variables && columnName.variables.length > 0;

          if (hasVariables) {
            gridColumn.name = this.lmFormatUtilService.formatMessageRule(columnName, dataViewSet.header);
            hasUpdate = true;
          }
        }
      }
    }

    // ----------------------------------------------------------
    // loop through columnGroups looking for a message rule with
    // variables. If found then format the value based on the
    // message rule and dataViewSet.
    // ----------------------------------------------------------
    if (columnGroups) {
      for (const columnGroup of columnGroups) {
        if (columnGroup.id) {
          const displayField: LMListDisplayField = listSpec.busListSpec.displayFields.find(listField => columnGroup.id === listField.groupLabelId);
          if (displayField && displayField.groupLabel && displayField.groupLabel.variables && displayField.groupLabel.variables.length > 0) {
            columnGroup.name = this.lmFormatUtilService.formatMessageRule(displayField.groupLabel, dataViewSet.header);
            hasUpdate = true;
          }
        }
      }
    }

    return hasUpdate;
  }

  public buildFilterCriteria(condition: SohoDataGridFilterCondition, columnDef: ListGridColumn): LMFilterFieldCriteria {
    let transformOptions: LMTransformOptions = {};
    let filterValue: string;
    let operator: string;
    let enteredFilterValue = '';

    if (columnDef.displayField.type === AppConstants.BCD
        || columnDef.displayField.type === AppConstants.PERCENT) {
      transformOptions = {
        decSize: columnDef.displayField.decSize,
        precision: columnDef.displayField.decSize
      };
    }

    if (Array.isArray(condition.value)) {
      filterValue = condition.value;
      operator = 'equals';

    } else if (columnDef.displayField.allowMultiSelections) {
      const allowZero = columnDef.displayField.type === AppConstants.BCD ||
                        columnDef.displayField.type === AppConstants.PERCENT ||
                        columnDef.displayField.type === AppConstants.NUMERIC;
      const multiFilterFieldValue = this.lmFormatUtilService.getMultipleFilterFieldValue(condition.value, columnDef.displayField.type, columnDef.displayField.decSize, columnDef.displayField.decSize, allowZero);
      filterValue = multiFilterFieldValue.formattedFilterValue;
      enteredFilterValue = multiFilterFieldValue.enteredFilterValue;
      operator = condition.operator;

    } else if (columnDef.displayField.type === AppConstants.TIMESTAMP) {
      if (columnDef.displayField?.displayDateOnly) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE;
      } else if (columnDef.displayField?.displayTimeOnly) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_TIME;
      } else if (columnDef.displayField?.displayHoursAndMinutesOnly) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES;
      } else if (columnDef.displayField?.displayDateAndHoursAndMinutes || columnDef.displayField?.limitToMinuteIncrements) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES;
      }
      filterValue = this.dateToFilterValue(condition.value, condition.operator, columnDef.displayField.type, transformOptions);
      operator = condition.operator;
    } else if (columnDef.displayField.type === AppConstants.TIME) {
      if (columnDef.displayField?.limitToMinuteIncrements) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES;
      }
      filterValue = this.dateToFilterValue(condition.value, condition.operator, columnDef.displayField.type, transformOptions);
      operator = condition.operator;
    } else if (columnDef.displayField.type === AppConstants.YYYYMMDD
      || columnDef.displayField.type === AppConstants.YYYYMM
      || columnDef.displayField.type === AppConstants.MMDD
      || columnDef.displayField.type === AppConstants.YYYY) {
      filterValue = this.dateToFilterValue(condition.value, condition.operator, columnDef.displayField.type, transformOptions);
      operator = condition.operator;
    } else {
      filterValue = this.lmFormatUtilService.toDataValue(condition.value, columnDef.displayField.type, transformOptions);
      operator = condition.operator;
    }

    return {
      operator: this.getLMOperator(operator),
      fieldId: columnDef.fieldId,
      id: columnDef.id,
      label: columnDef.name,
      value: filterValue,
      isRequired: columnDef.isRequiredSearch,
      enteredFilterValue
    };
  }

  private dateToFilterValue(value: string, operator: string, type: string, transformOptions?: LMTransformOptions): string {
    let filterValue: string;
    let startDate: string;
    let endDate: string;
    const range = value.split(' - ');

    if (operator === 'in-range') {
      if (range.length === 2) {
        startDate = this.lmFormatUtilService.toDataValue(range[0].trim(), type);
        endDate = this.lmFormatUtilService.toDataValue(range[1].trim(), type);
      } else {
        // Provide a default end date to prevent Internal Server Errors until
        // https://github.com/infor-design/enterprise/issues/3337 is resolved
        startDate = this.lmFormatUtilService.toDataValue(range[0].trim(), type);
        endDate = this.getDefaultValue(type);
      }
      filterValue = startDate + ',' + endDate;
    } else {
      if (range.length > 1) {
        value = range[0];
      }
      filterValue = filterValue = this.lmFormatUtilService.toDataValue(value, type, transformOptions);
    }

    return filterValue;
  }

  private getDefaultValue(type: string): any {
    const defaultValue = '';

    if (type === AppConstants.YYYYMMDD) {
      return '00000000';

    } else if (type === AppConstants.YYYYMM) {
      return '000000';

    } else if (type === AppConstants.MMDD) {
      return '0000';

    } else if (type === AppConstants.YYYY) {
      return '0000';

    } else if (type === AppConstants.TIMESTAMP) {
      return '0000000000000000';
    }

    return defaultValue;
  }

  public getSohoOperator(lmOperator: LMFilterFieldOperator | string): SohoFilterFieldOperator {
    const foundEntry: OperatorMapping = AppConstants.OperatorMap.find(operatorEntry => operatorEntry.lmOperator === lmOperator);
    return foundEntry ? foundEntry.sohoOperator : null;
  }

  public getLMOperator(sohoOperator: SohoFilterFieldOperator | string): LMFilterFieldOperator {
    const foundEntry: OperatorMapping = AppConstants.OperatorMap.find(operatorEntry => operatorEntry.sohoOperator === sohoOperator);
    return foundEntry ? foundEntry.lmOperator : null;
  }

  /**
   * Builds the pageSizeChooser values and will include a showLines from the spec
   * if one is specified. If not showing a pagesizechooser then it will return an
   * empty array.
   * @param listSpec The list spec to determine if showLines is set.
   */
  public getPageSizesArray(listSpec: LMListSpec): number[] {
    if (!this.showPageSizeChooser(listSpec)) {
      return [];
    }

    let pageSizeArray = AppConstants.PAGE_SIZES;
    if (listSpec.busListSpec.showLines !== undefined) {
      if (!pageSizeArray.includes(listSpec.busListSpec.showLines)) {
        pageSizeArray = pageSizeArray.concat(listSpec.busListSpec.showLines).sort((a, b) => a - b);
      }
    }

    return pageSizeArray;
  }

  /**
   * Get the page size based on first personalization, then showLines, then spec page size (default).
   * If not showing the page size chooser then no personalization is valid so return showLines and
   * then the default page size.
   * @param listSpec The list spec to determine if showLines is set.
   * @param listPersonalization The list personalization state object.
   */
  public getPageSize(listSpec: LMListSpec, listPersonalization: LMListPersonalization): number {
    if (this.showPageSizeChooser(listSpec)) {
      return listPersonalization.pageSize || listSpec.busListSpec.showLines || listSpec.pageSize;
    } else {
      return listSpec.busListSpec.showLines || listSpec.pageSize;
    }
  }

  /**
   * Whether or not the pageSizeChooser dropdown should be displayed or not.
   * @param listSpec The list spec to determine if a pageSizeChooser should be shown.
   */
  public showPageSizeChooser(listSpec: LMListSpec) {
    return !this.lmListSpecUtilService.isDashBoardContent(listSpec) && !this.lmListSpecUtilService.isColumnarView(listSpec);
  }

  /**
   * Return whether this list is the target of a use-as-search form. In that case the list should
   * start out empty and only fill out if the user clicks search.
   * @param listSpec
   */
  public isUseAsSearchTargetList(listSpec: LMListSpec) {
    if (listSpec.listType !== 'select' && listSpec.busListSpec.searchForm && !listSpec.busListSpec.searchForm.useSearchAsFilter) {
      return true;
    }

    return false;
  }

  public displayPagingConfirmation(): Observable<boolean> {
    return new Observable <boolean>((observer) => {
      const dialogId = 'DataHasChanged';
      const buttons = [
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            observer.next(false);
            observer.complete();
            modal.close();
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          click: (e, modal) => {
            observer.next(true);
            observer.complete();
            modal.close();
          }
        }
      ];

      // Show the confirmation message dialog.
      this.sohoMessageService
        .message()
        .message(Soho.Locale.translate('ContinueWithoutSaving'))
        .title(Soho.Locale.translate(dialogId))
        .buttons(buttons)
        .open();
    });
  }

  /**
   * For lists that don't have 'use as filter' the list is supposed to initially display w/o any records.
   * But we also want to allow the user to search w/o entering in search criteria and have all recs returned.
   * And then want clear-search on the search form to clear any search results whether from criteria or just
   * clicking search w/o any search criteria.
   *
   * To know the different we distinguish between these 3 state:
   * filterCriteria === null - means clear the search and show the list empty and return 0 for nbrRecs
   * filterCriteria === undefined - initial page load
   * filterCriteria ==== [] - means that a filter is to be executed whether there are any array entries
   * or not. return the defaultPageSize
   */
  getInitialNumberOfRecs(listSpec: LMListSpec, listState: LMListState, defaultPageSize: number) {
    let initialNbrRecs = defaultPageSize;
    if (this.isUseAsSearchTargetList(listSpec)) {
      // if loading from pageSate and filterCriteria is null or undefined then return 0 initial recs
      // else let it fall through to return the defaultPageSize.
      if (!listState || listState.filterCriteria === null || listState.filterCriteria === undefined) {
        initialNbrRecs = 0;
      }
    }

    return initialNbrRecs;
  }

  public getInitialActivatedRow(listSpec: LMListSpec, listState: LMListState, fromDataView: LMDataView) {
    // let activatedRow = -1;
    // if (resetSelection || (this.listSpec.dependentList && this.listState.fromResourceId !== this.fromDataView.resourceId)) {
    //   activatedRow = 0;
    // } else {
    //   activatedRow = this.listState.activatedRow && this.listState.activatedRow > -1 ?  this.listState.activatedRow  : 0;
    // }
    // return activatedRow;

    if (!listSpec.dependentList || listState.fromResourceId === fromDataView.resourceId) {
      if (listState && listState.activatedRow > -1) {
        // ---------------------------------------------------------------------------------------------
        // return activated row of the listState.activatedRow for all NON DEPENDENT lists but ONLY
        // dependent lists where the fromDataView hasn't changed.
        // ---------------------------------------------------------------------------------------------
        return listState.activatedRow;
      }
    }

    return 0;
  }

  getInitialSelectedRows(listSpec: LMListSpec, listState: LMListState, dataViewSet: LMDataViewSet, fromDataView: LMDataView): number[] {
    if (!listSpec.dependentList || listState.fromResourceId === fromDataView.resourceId) {
      if (listState && listState.selectedRows && listState.selectedRows.length > 0) {
        // ---------------------------------------------------------------------------------------------
        // return selected rows of the listState.selectedRows for all NON DEPENDENT lists but ONLY
        // dependent lists where the fromDataView hasn't changed.
        // ---------------------------------------------------------------------------------------------
        return listState.selectedRows.filter(row => row < dataViewSet.data.length);
      }
    }

    return undefined;
  }

  public isFieldCopyable(fieldId: string, field: LMField, dataView: LMDataView): boolean {
    if (!fieldId || !field) {
      return false;
    }

    if (field.symbolicKey || field.symbolicVersionKey) {
      return false;
    }

    if (fieldId.includes('_prd_')) {
      const groupFieldId = fieldId.split('_prd_')[0];
      const groupField = dataView.fields[ groupFieldId ];
      if (groupField && groupField.type === 'group' && (groupField.symbolicKey || groupField.symbolicVersionKey)) {
        return false;
      }
    }

    return true;
  }

  public isAutoFilterEnabled(): boolean {
    if (this.lmContextService.getFilterBarModeSetInCsk() !== undefined) {
      return !(this.lmContextService.getFilterBarModeSetInCsk() === 'power');
    } else if (this.lmGlobalPersonalizationService.getGlobalPersonalization()?.automaticSearch !== undefined) {
      return this.lmGlobalPersonalizationService.getGlobalPersonalization().automaticSearch !== 'passive';
    } else if (this.lmContextService.isFilterBarOnPowerMode()) {
      return !this.lmContextService.isFilterBarOnPowerMode();
    }

    return true;
  }

  public hasSearchForm(listSpec: LMListSpec, isDashBoardContent: boolean) {
    if (listSpec?.busListSpec) {

      // don't allow search forms mingle widgets
      if (listSpec.showAsMingleWidget) {
        return false;
      }

      // don't allow search forms in dashboard panels
      if (isDashBoardContent) {
        return false;
      }

      return (listSpec.busListSpec.searchForm !== undefined);
    }
    return false;
  }

  public searchFormOnLeft(listSpec: LMListSpec) {
    return listSpec.busListSpec?.searchForm?.showSearchFormOnLeft;
  }
}
